import { some } from "lodash";
import { Loader } from "lucide-react";
import { FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Link } from "react-router";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  useDeleteSKUMutation,
  useTransferSKUMutation,
  useUpdateSKUMutation,
} from "@/data/mutations";
import { SKU } from "@/data/types";
import { useDialogState } from "@/hooks/use-dialog-state";
import { SKUFormValues } from "@/lib/forms";
import { canEditSKU } from "@/lib/permissions";
import { upcFormatter } from "@/lib/utils";
import useStore from "@/state";

type Props = {
  sku: SKU;
};

const resolveDirtyFields = (
  fields: Record<string, boolean | boolean[] | undefined>,
) => {
  return some([
    fields.name,
    fields.sku_code,
    fields.upc,
    fields.notes,
    fields.analytics_url,
  ]);
};

export const OverviewTab: FC<Props> = ({ sku }) => {
  const updateSKUMutation = useUpdateSKUMutation(sku.id);
  const transferSKUMutation = useTransferSKUMutation();
  const deleteSKUMutation = useDeleteSKUMutation();
  const { openDialog } = useDialogState();
  const { session } = useStore();

  const form = useFormContext<SKUFormValues>();

  const handleTransferSKU = () => {
    openDialog({ name: "transfer-sku", params: { skuId: sku.id } });
  };

  const handleResequence = () => {
    openDialog({ name: "resequence-sku", params: { skuId: sku.id } });
  };

  const handleDeleteSKU = () => {
    openDialog({ name: "delete-sku", params: { skuId: sku.id } });
  };

  const handleMoveTags = () => {
    openDialog({
      name: "move-tags",
      params: { skuId: sku.id, tagCount: sku.tags_count },
    });
  };

  const handleSubmit = async (values: SKUFormValues) => {
    await updateSKUMutation.mutateAsync({
      name: values.name,
      notes: values.notes,
      sku_code: values.sku_code,
      upc: values.upc,
      analytics_url: values.analytics_url,
    });

    form.reset({}, { keepValues: true });
  };

  const hasPermission = useMemo(
    () => canEditSKU(session?.permissions),
    [session?.permissions],
  );

  const isDirty = resolveDirtyFields(form.formState.dirtyFields);

  const isSubmitEnabled = useMemo(() => {
    const isValid = form.formState.isValid;
    const isPending = updateSKUMutation.isPending;

    return hasPermission && isDirty && isValid && !isPending;
  }, [
    hasPermission,
    isDirty,
    form.formState.isValid,
    updateSKUMutation.isPending,
  ]);

  return (
    <div className="w-full">
      <div className="flex gap-6 mt-6">
        <Card className="flex-1 p-6 space-y-6">
          <p className="text-base text-muted-foreground">Tags in SKU</p>
          <p className=" font-medium text-2xl/tight">{sku.tags_count}</p>
          <Link to={`/tags?templateId=${sku.id}`} className="underline">
            View in Tags
          </Link>
        </Card>
        <Card className="flex-1 p-6 space-y-6">
          <p className="text-base text-muted-foreground">Page Views in SKU</p>
          <p className="font-medium text-2xl/tight">
            {sku.tag_view_verifications_count}
          </p>
          <Link
            to={`/verifications?templateId=${sku.id}&verifyType=user_view`}
            className="underline"
          >
            View in Verifications
          </Link>
        </Card>
      </div>

      <h3 className="scroll-m-20 text-2xl font-medium tracking-tight mt-8">
        Details
      </h3>

      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <FormField
          control={form.control}
          name="name"
          disabled={!hasPermission}
          render={({ field }) => (
            <FormItem className="mt-6">
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Spring Trade Show" {...field} />
              </FormControl>
              <FormDescription>
                This is the display name of the SKU and its associated Digital
                Product. Max 75 characters.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-6 mt-6">
          <FormField
            control={form.control}
            name="sku_code"
            disabled={!hasPermission}
            render={({ field }) => (
              <FormItem className="grid grid-rows-[auto_auto_1fr]">
                <FormLabel>SKU (Stock Keeping Unit)</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage>
                  If you have your own unique alphanumeric code
                </FormMessage>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="upc"
            disabled={!hasPermission}
            render={({ field }) => (
              <FormItem className="grid grid-rows-[auto_auto_1fr]">
                <FormLabel>Barcode (UPC)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onChange={(e) =>
                      field.onChange(upcFormatter(e.target.value, [1, 5, 5, 1]))
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="notes"
          disabled={!hasPermission}
          render={({ field }) => (
            <FormItem className="mt-6">
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Input placeholder="Our project with Acme Corp" {...field} />
              </FormControl>
              <FormDescription>
                Use this to add any additional notes about this SKU. This will
                not be displayed publicly.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="analytics_url"
          disabled={!hasPermission}
          render={({ field }) => (
            <FormItem className="mt-6">
              <FormLabel>Looker Analytics</FormLabel>
              <FormControl>
                <Input
                  placeholder="Link to Looker analytics dashboard to view tag activity."
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button className="mt-6" type="submit" disabled={!isSubmitEnabled}>
          {updateSKUMutation.isPending && <Loader className="animate-spin" />}
          Save
        </Button>

        <Card className="mt-6">
          <Accordion type="single" collapsible className="px-4">
            <AccordionItem value="danger-zone">
              <AccordionTrigger>Danger Zone</AccordionTrigger>
              <AccordionContent className="pt-2">
                <div className="flex justify-between">
                  <div>
                    <p className="font-medium">Transfer</p>
                    <p className="font-medium text-sm text-muted-foreground">
                      Transfer the SKU to a different organization.
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={handleTransferSKU}
                    disabled={transferSKUMutation.isPending || !hasPermission}
                    className="w-[100px]"
                  >
                    {transferSKUMutation.isPending && (
                      <Loader className="w-4 h-4 animate-spin" />
                    )}
                    Transfer
                  </Button>
                </div>
                <div className="flex justify-between mt-6">
                  <div>
                    <p className="font-medium">Move Tags</p>
                    <p className="font-medium text-sm text-muted-foreground">
                      Choose a group of tags to move from one SKU to another
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={handleMoveTags}
                    disabled={transferSKUMutation.isPending || !hasPermission}
                    className="w-[100px]"
                  >
                    Move
                  </Button>
                </div>
                <div className="flex justify-between mt-6">
                  <div>
                    <p className="font-medium">Resequence</p>
                    <p className="font-medium text-sm text-muted-foreground">
                      Reorder serial numbers of tags in this SKU sequentially by
                      programming time
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleResequence}
                    disabled={deleteSKUMutation.isPending || !hasPermission}
                    className="w-[100px]"
                  >
                    {deleteSKUMutation.isPending && (
                      <Loader className="w-4 h-4 animate-spin" />
                    )}
                    Resequence
                  </Button>
                </div>
                <div className="flex justify-between mt-6">
                  <div>
                    <p className="font-medium">Delete</p>
                    <p className="font-medium text-sm text-muted-foreground">
                      Delete SKU and all of its associated tags from the
                      organization
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDeleteSKU}
                    disabled={deleteSKUMutation.isPending || !hasPermission}
                    className="w-[100px]"
                  >
                    {deleteSKUMutation.isPending && (
                      <Loader className="w-4 h-4 animate-spin" />
                    )}
                    Delete
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>
      </form>
    </div>
  );
};
